<template>
	<div class="input-wrapper">
		<template v-if="labelText">
			<div v-if="$slots.tooltip" class="row justify-content-between flex-nowrap has-tooltip">
				<label
					:id="uniqueLabel"
					class="label form-label col-auto"
					v-html="labelText"
				/>
				<slot name="tooltip" />
			</div>
			<label
				v-else
				:id="uniqueLabel"
				class="label form-label"
				v-html="labelText"
			/>
		</template>

		<div :class="{'pound-symbol': inputType === 'currency'}">
			<div class="input-i-wrapper">
				<input v-model.trim="fieldInput"
					:aria-labelledby="uniqueLabelUsingAria"
					class="input form-control"
					:class="{
					'input--valid' : !v$.fieldInput.$invalid && fieldInputLengthGreaterThanZero,
				'input--error': v$.fieldInput.$error,
				'input-format__currency': inputType === 'currency',
				'input-format__postcode': inputType === 'postcode',
				'input-format__houseno': inputType === 'houseno',
				'input-format__age': inputType === 'age',
				}"
				:aria-invalid="v$.fieldInput.$error"
				:aria-describedby="v$.fieldInput.$error ? uniqueErrorTextId : undefined"
				:maxLength="textType.maxLength"
				:type="textType.inputType"
				:inputmode="this.isNumericType ? 'numeric' : undefined"
				:placeholder="textType.placeholder"
				:autocomplete="textType.autocomplete"
				@blur="handleBlur"
				@focus="handleFocus"
				@input="handleInput"
				@keydown="handleKeyDown"
				>
				<div v-if="$slots.suffix">
					<slot name="suffix" />
				</div>
			</div>
			<transition name="fade">
				<p
					:id="uniqueErrorTextId"
					ref="errorText"
					v-if="v$.fieldInput.$error"
					class="error-text"
					v-html="errorTextComputed"
				/>
				<p
					v-else-if="furtherInformationText"
					class="further-information-text"
					v-html="furtherInformationText"
				/>
				<p
					v-else-if="supportText"
					class="support-text"
					v-html="supportText"
				/>
			</transition>
		</div>
	</div>
</template>
<script>
import { useVuelidate } from '@vuelidate/core';
import { requiredIf, email, minLength, minValue, maxValue, helpers } from '@vuelidate/validators';
import { intervalToDuration, parse, isValid, max } from 'date-fns';
import { cleanStrForElemId } from '../../../lib/utils/helpers';

export default {
	props: {
		inputType: {
			type: String,
			default: 'text',
		},
		labelText: {
			type: String,
			default: '',
		},
		furtherInformationText: {
			type: String,
			default: '',
		},
		errorText: {
			type: String,
			default: '',
		},
		supportText: {
			type: String,
			default: '',
		},
		required: {
			type: Boolean,
			default: false,
		},
		maxLength: {
			type: Number,
			default: null,
		},
		prefillInput: {
			type: String,
			default: '',
			required: false,
		},
		minimumValue: {
			type: Number,
			default: 0,
			required: false,
		},
		maximumValue: {
			type: Number,
			default: 999999999,
			required: false,
		},
		manualInputDirtier: {
			type: Boolean,
			default: false,
		},
		autocomplete: {
			type: String,
			default: '',
		},
		allowZero: {
			type: Boolean,
			default: false,
		},
		noFloat: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['field-input', 'input-error'],
	data() {
		return {
			v$: useVuelidate(),
			fieldInput: '',
			fieldInputLengthGreaterThanZero: false, // We need to track this because we cant check length of numeric input directly
		};
	},
	computed: {
		textType() {
			switch (this.inputType) {
				case 'postcode':
					return {
						inputType: 'text',
						maxLength: 9,
						autocomplete: 'postal-code',
					};
				case 'email':
					return {
						inputType: 'email',
						autocomplete: 'email',
					};
				case 'currency':
					return {
						placeholder: '',
						inputType: 'text',
						maxLength: 8,
					};
				case 'phoneNumber':
					return {
						inputType: 'tel'
					};
				case 'dob':
					return {
						placeholder: 'DD/MM/YYYY',
						inputType: 'text',
						maxLength: 10,
						autocomplete: 'bday',
					};
				case 'NiN':
					return {
						inputType: 'text',
					};
				case 'age':
					return {
						inputType: 'number',
						maxLength: 2,
					};
				case 'years':
					return {
						inputType: 'text',
						maxLength: 2,
					};
				default:
					return {
						inputType: 'text',
						maxLength: this.maxLength,
						autocomplete: this.autocomplete || null
					};
			}
		},

		isNumericType() {
			return this.inputType === 'currency'
				|| this.inputType === 'age'
				|| this.inputType === 'years';
		},

		// DOB is the only field that has separate error messages
		errorTextComputed() {
			if (this.inputType === 'dob' && !this.v$.fieldInput.isValidDate.$response) {
				return 'Please enter a valid date of birth in the format 01 08 1970';
			}
			else if (this.inputType === 'dob' && !this.v$.fieldInput.isValidAge.$response) {
				return 'The date of birth you entered would make you ineligible for our With Profits ISA. You must be between 18 and 74 to apply.';
			}
			else {
				return this.errorText;
			};
		},
		uniqueLabel() {
			return cleanStrForElemId(this.labelText) + '-label';
		},
		uniqueErrorTextId() {
			return cleanStrForElemId(this.labelText) + '-error';
		},
		uniqueLabelUsingAria() {
			if (!this.labelText && !this.$slots.tooltip && this.$attrs['aria-labelledby']) {
				return cleanStrForElemId(this.$attrs['aria-labelledby']);
			} else if (this.labelText) {
				return this.uniqueLabel;
			} else {
				return null;
			}
		}
	},
	mounted() {
		if (this.prefillInput !== null && this.prefillInput !== '') {
			this.fieldInput = this.prefillInput;
			this.handleBlur();
		}
	},
	watch: {
		prefillInput(newValue, oldValue) {
			this.fieldInput = newValue;
			this.handleBlur();
		},
		manualInputDirtier(newValue, oldValue) {
			if (newValue) {
				this.v$.fieldInput.$dirty = true;
			}
		},
		/**
		 * Emit input event on vuelidate change.
		 */
		v$() {
			this.handleInput();
		}
	},
	methods: {
		formatDOB(event) {
			let cursorPosition = event.target.selectionStart;

			if (['Backspace', 'Delete', 'Tab'].includes(event.key)
				|| [37, 39, 35].includes(event.keyCode)
			) { }
			else if (event.key >= 0 && event.key <= 9) {
				if (event.target.value.length == 2) {
					const formattedDate = event.target.value.replace(/[^0-9]/g, '').replace(/(\d{2})/, '$1/');
					this.fieldInput = formattedDate;
				}
				else if (event.target.value.length >= 5) {
					const formattedDate = event.target.value.replace(/[^0-9]/g, '').replace(/(\d{2})(\d{2})/, '$1/$2/');
					this.fieldInput = formattedDate;
				}
				else if (event.target.value.length == 10) {
					const formattedDate = event.target.value.replace(/[^0-9]/g, '').replace(/(\d{2})(\d{2})(\d{4})/, '$1/$2/$3');
					this.fieldInput = formattedDate;
				}
			}
			else {
				event.preventDefault();
			}

			event.target.setSelectionRange(cursorPosition, cursorPosition);
		},
		handleBlur() {
			this.v$.fieldInput.$dirty = true;

			this.fieldInput = this.addChars(this.fieldInput);

				if (this.v$.fieldInput.$error) {
					this.$emit('input-error', true)
				}
				else if (!this.v$.fieldInput.$error) {
					this.$emit('input-error', false)
				}
			},
			handleFocus() {
				this.fieldInput = this.removeChars(this.fieldInput);
			},
			handleKeyDown(event) {
				if (this.inputType === 'dob') {
					this.formatDOB(event);
				}
				if (this.inputType === 'currency' && event.key.length === 1 && /\D/.test(event.key)) {
					event.preventDefault();
				}
			},
			handleInput() {
				const value = this.removeChars(this.fieldInput);
				const valueLength = value.length;
				const valueLengthWhenConverted = value.toString().length;
				const isNumericValue = !isNaN(value);

				const valueLengthCheck = isNumericValue ? valueLengthWhenConverted : valueLength;
				const isValid = (!this.v$.fieldInput.$invalid && valueLengthCheck > 0);

				this.fieldInputLengthGreaterThanZero = valueLengthCheck > 0;

				// console.log("TextInput isValid: ", isValid);
				// console.log("TextInput value: ", value);
				// console.log("TextInput value length: ", value.length);
				// console.log("TextInput valueLengthWhenConverted: ", valueLengthWhenConverted);
				// console.log("TextInput isNumericValue: ", isNumericValue);

				this.$emit('field-input', {
					value,
					isValid,
					hasError: this.v$.fieldInput.$error
				});
			},
			/**
			 * Returns a value with characters added, depending on the input type.
			 * @param {string} value - The value to add characters to
			 */
			addChars(value) {
				switch(this.inputType) {
					case 'currency':
						return (parseFloat(value) || 0)
							.toLocaleString('en', { useGrouping: true });
					case 'NiN':
						return value
							.replace(/\s/g, '')
							.toUpperCase()
							.replace(/(.{2})(?=.)/g, '$1 ');
					case 'years':
						return `${value} years`;
					case 'percentage':
						return `${value}%`;
					default:
						return value;
				}
			},
			/**
			 * Returns a value as a string only containing numbers.
			 * @param {string} value - The value to remove characters from
			 */
			removeChars(value) {
				switch (this.inputType) {
					case 'currency':
					case 'NiN':
						return value.replace(/[,\s]+/g, '');
					case 'years':
						return value.replace(" years", "");
					case 'percentage':
						return value.replace("%", "");
					default:
						return value;
				}
			},
			calculateAge() {
				const todaysDate = new Date();

			const birthDate = parse(this.fieldInput, 'dd/MM/yyyy', new Date());
			let age = intervalToDuration({
				start: birthDate,
				end: todaysDate,
			}).years;
			if (age > 74 || age < 18) {
				return false;
			}
			return true;
		},
	},
	validations() {
		switch (this.inputType) {
			case 'postcode':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						postcodeValidator,
					},
				};
			case 'email':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						email,
					},
				};
			case 'phoneNumber':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						phoneValidator,
						minLength: minLength(11),
					},
				};
			case 'currency':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						isBetweenMinAndMax: (value) => {
							if (this.allowZero && value === '0') {
								return true;
							}
							return value.replace(/[,\s]+/g, '') >= this.minimumValue && value.replace(/[,\s]+/g, '') <= this.maximumValue;
						},
						isNotAFloat: helpers.withMessage('Please enter a whole number', (value) => {
							return !this.noFloat || Number.isInteger(Number(value));
						}),
					},
				};
			case 'dob':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						minLength: minLength(10),
						isValidDate: (value) => {
							if (value.length === 10) {
								const parsedDate = parse(value, 'dd/MM/yyyy', new Date());
								return isValid(parsedDate);
							}
						},
						isValidAge: () => {
							return this.calculateAge();
						},

					},
				};
			case 'NiN':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						minLength: minLength(9),
						// NOT BEING USED! NationalInsuranceNumberValidator,
					},
				};
			case 'age':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						minValue: minValue(this.minimumValue),
						maxValue: maxValue(this.maximumValue),
						isNotAFloat: helpers.withMessage('Please enter a whole number', (value) => {
							return !this.noFloat || Number.isInteger(Number(value));
						}),
					}
				};
			case 'years':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						isBetweenMinAndMax: (value) => {
							const cleaned = value.replace("years", "");
							return cleaned >= this.minimumValue && cleaned <= this.maximumValue;
						},
						isNotAFloat: helpers.withMessage('Please enter a whole number', (value) => {
							return !this.noFloat || Number.isInteger(Number(value));
						}),
					}
				}
			case 'percentage':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						isBetweenMinAndMax: (value) => {
							const cleaned = value.replace("%", "");
							return cleaned >= this.minimumValue && cleaned <= this.maximumValue;
						},
					}
				}
			default:
				return {
					fieldInput: {
						required: requiredIf(this.required),
						isNotAFloat: not(decimal(value))
					},
				};
		}
	},
};

/**
 * @typedef {{
 * value: string;
 * isValid: boolean;
 * }} TextInputData
 */

</script>

<style scoped lang="scss">
	$error: #d72d41;
	$default-text-color: #41474f;

	.further-information-text {
		font-size: 6px;
		background-image: url('/assets/images/furtherInformationQuestionMark.svg');
		background-position: left center;
		background-repeat: no-repeat;
		background-size: 6px;
		padding-left: 8px;
		margin-top: 2px;
		color: $default-text-color;
	}

	.support-text {
		font-size: 0.9375rem;
		margin-top: 8px;
		color: #41474f;
	}

	.input {
		// TODO: Can we delete these?
		height: 56px;
		padding-right: 30px;

		&:not(.input--valid, .input--error):focus {
			border-color: #40474f;
			outline: none;
			border-width: 1px;
		}
	}
</style>
